/**
 * EHR-style frequency orders.
 *
 *   "2w9"        → 2 visits/week for 9 weeks
 *   "1-3w9"      → 1 to 3 visits/week for 9 weeks; the plan defaults to the midpoint
 *   "3w2, 2w7"   → a sequence: 3/week for 2 weeks, then 2/week for 7 weeks
 *   "1q2w6"      → 1 visit every 2 weeks for 6 weeks (every other week)
 *   "7w1"        → daily for a week (the interim-order shape)
 *   "eval"       → evaluation only; frequencies arrive after the SOC assessment
 *
 * Ranges keep their bounds so authorized users can adjust within what was ordered.
 */

export interface FrequencySegment {
  minPerWeek: number;
  maxPerWeek: number;
  /** Visits per visiting week the plan uses by default (midpoint, halves rounded up). */
  perWeek: number;
  /** 1 = every week; 2 = every other week (the `q2w` token). */
  everyWeeks: number;
  weeks: number;
}

export interface ParsedFrequency {
  evalOnly: boolean;
  segments: FrequencySegment[];
  totalVisits: number;
  totalWeeks: number;
  hasRange: boolean;
}

const SEGMENT = /^(\d+)(?:-(\d+))?(?:q(\d+))?w(\d+)$/i;

export class FrequencyParseError extends Error {
  constructor(public readonly pattern: string, message: string) {
    super(`Cannot parse frequency "${pattern}": ${message}`);
  }
}

export function midpoint(min: number, max: number): number {
  return Math.round((min + max) / 2);
}

export function parseFrequency(pattern: string): ParsedFrequency {
  const trimmed = pattern.trim();
  if (/^eval(uation)?(\s*(&|and)\s*treat)?$/i.test(trimmed)) {
    return { evalOnly: true, segments: [], totalVisits: 0, totalWeeks: 0, hasRange: false };
  }
  const tokens = trimmed
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length === 0) throw new FrequencyParseError(pattern, "empty");
  const segments: FrequencySegment[] = tokens.map((token) => {
    const m = SEGMENT.exec(token);
    if (!m) throw new FrequencyParseError(pattern, `unrecognized segment "${token}"`);
    const min = Number(m[1]);
    const max = m[2] !== undefined ? Number(m[2]) : min;
    const everyWeeks = m[3] !== undefined ? Number(m[3]) : 1;
    const weeks = Number(m[4]);
    if (max < min) throw new FrequencyParseError(pattern, `range ${min}-${max} is inverted`);
    if (weeks < 1) throw new FrequencyParseError(pattern, "weeks must be at least 1");
    if (everyWeeks < 1) throw new FrequencyParseError(pattern, "interval must be at least 1 week");
    return { minPerWeek: min, maxPerWeek: max, perWeek: midpoint(min, max), everyWeeks, weeks };
  });
  return {
    evalOnly: false,
    segments,
    totalVisits: segments.reduce((s, seg) => s + frontLoadedWeeklyCounts(seg).reduce((a, b) => a + b, 0), 0),
    totalWeeks: segments.reduce((s, seg) => s + seg.weeks, 0),
    hasRange: segments.some((seg) => seg.minPerWeek !== seg.maxPerWeek),
  };
}

/**
 * Visits per week across a segment's weeks, front-loaded: the midpoint total is
 * kept, but range weeks lean toward the maximum early and the minimum late so
 * ordered care is banked before attrition can drop a period below its threshold.
 * An every-other-week segment visits on its first week and every `everyWeeks`
 * after: `1q2w6` → [1, 0, 1, 0, 1, 0].
 */
export function frontLoadedWeeklyCounts(segment: FrequencySegment): number[] {
  const { minPerWeek, maxPerWeek, perWeek, weeks } = segment;
  const every = segment.everyWeeks ?? 1;
  if (every > 1) {
    return Array.from({ length: weeks }, (_, i) => (i % every === 0 ? perWeek : 0));
  }
  if (minPerWeek === maxPerWeek) return Array(weeks).fill(perWeek);
  const total = perWeek * weeks;
  const counts = Array(weeks).fill(minPerWeek) as number[];
  let remaining = total - minPerWeek * weeks;
  for (let i = 0; i < weeks && remaining > 0; i++) {
    const add = Math.min(maxPerWeek - minPerWeek, remaining);
    counts[i] += add;
    remaining -= add;
  }
  return counts;
}

/** EHR style, round-tripping what was parsed ("3w2, 2w7", "1q2w6"). */
export function formatFrequency(parsed: ParsedFrequency): string {
  if (parsed.evalOnly) return "eval & treat";
  return parsed.segments
    .map((s) => `${s.minPerWeek === s.maxPerWeek ? s.minPerWeek : `${s.minPerWeek}-${s.maxPerWeek}`}${s.everyWeeks > 1 ? `q${s.everyWeeks}` : ""}w${s.weeks}`)
    .join(", ");
}

/** Plain words for the plan page: "2 a week × 3 wks, then every other week × 6 wks". */
export function describeFrequency(parsed: ParsedFrequency): string {
  if (parsed.evalOnly) return "evaluation, frequencies to follow";
  return parsed.segments
    .map((s) => {
      const n = s.minPerWeek === s.maxPerWeek ? `${s.perWeek}` : `${s.minPerWeek}–${s.maxPerWeek}`;
      const cadence = s.everyWeeks === 1 ? `${n} a week` : s.everyWeeks === 2 ? (s.perWeek === 1 ? "every other week" : `${n} every other week`) : `${n} every ${s.everyWeeks} weeks`;
      return `${cadence} × ${s.weeks} wk${s.weeks === 1 ? "" : "s"}`;
    })
    .join(", then ");
}
